import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `marketplace.maker` em português do Brasil.
 *
 * Declarado `: Messages['marketplace']['maker']`, nunca inferido — é isso que garante a
 * completude (PRD-27 LOC-011): uma chave que falta aqui, uma chave a mais, ou uma função cuja
 * assinatura mudou, é uma falha de `bun run typecheck`.
 *
 * Registro `você`, informal-profissional (LOC-061), com o vocabulário ratificado no ADR-0018:
 * *grupo* nunca *time*; *base de conhecimento* nunca *fonte* (fonte é a citação); *pessoa*
 * nunca *usuário* quando se fala de gente; *execução* e não *rodada*; *etapa* e não *passo*.
 * As frases fixas do D-37 vêm do ADR palavra por palavra — *Quem pode usar*, *Pertence a*,
 * *todas as pessoas que entram*, *Só quem mantém*, *Aguardando revisão*.
 *
 * ── Concordância, onde ela foi desenhada para fora ──
 *
 * Adjetivos e particípios concordam com o substantivo. *Base de conhecimento* é feminina, logo
 * "anexada" e "Pronta"; *agente* é masculino, logo "Publicado"; *execução* é feminina, logo
 * "Essa execução não terminou". Onde uma frase servia a entidades de gêneros diferentes, ou a
 * um sujeito cujo NÚMERO varia (uma pessoa, um grupo, todo mundo), a frase foi reescrita para
 * não depender de concordância nenhuma em vez de escolher um gênero (PRD-27 D-21). O caso mais
 * visível está em `marketplace.publish`, onde o sujeito chega pronto de fora.
 *
 * ── Plurais ──
 *
 * Sempre `Intl.PluralRules`, nunca `n === 1`: o português do Brasil põe o ZERO no singular
 * ("0 documento"), e o inglês no plural ("0 documents"). Uma comparação de dois ramos já está
 * errada em uma das duas línguas, em todo estado vazio, sem nada falhar (D-19).
 */

type Counting = { locale: Locale; formatCount: (value: number) => string }
type Language = { locale: Locale }

const countOf = (fmt: Counting, count: number, forms: { one: string; other: string }): string =>
  `${fmt.formatCount(count)} ${pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other}`

const people = (fmt: Counting, count: number): string => countOf(fmt, count, { one: 'pessoa', other: 'pessoas' })

const byCount = (fmt: Language, count: number, forms: { one: string; other: string }): string =>
  pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other

export const maker: Messages['marketplace']['maker'] = {
  people,
  sized: (fmt, group) => `${group.name} (${people(fmt, group.memberCount)})`,

  builder: {
    newTitle: 'Novo agente',
    newLead: 'Dê um nome para começar. Todo o resto pode mudar até você publicar.',
    nameLabel: 'Nome',
    namePlaceholder: 'Ajudante de Despesas',
    ownerLead:
      'Um agente de grupo é mantido por todas as pessoas que gerenciam o grupo, e conta no limite do grupo, não no seu.',
    create: 'Começar a criar',
    creating: 'Criando…',
    createFailed: 'Não foi possível criar esse agente.',
    quotaReached:
      'Você já tem o número máximo de agentes permitido. Descontinue um que não usa mais, ou peça a um administrador para aumentar seu limite.',
    notAllowed: 'Você pode criar um agente para você, ou para um grupo que você gerencia.',
    nameRequired: 'Dê um nome ao agente primeiro.',
    backToList: 'Seus agentes',
    loading: 'Abrindo o editor…',
    loadFailed: 'Não foi possível abrir este agente.',
    notAvailableTitle: 'Este agente não está disponível',
    notAvailableBody:
      'Ele pode ter sido removido, ou pode não estar compartilhado com você. Veja os agentes que você pode usar.',
    tryAgain: 'Tentar novamente',
    unsaved: 'Alterações não salvas',
    save: 'Salvar',
    saving: 'Salvando…',
    saved: 'Salvo',
    revert: 'Desfazer alterações',
    changeNoteLabel: 'O que mudou?',
    changeNotePlaceholder: 'Adicionei a política de viagens de 2026 — opcional, fica no histórico',
    saveFailed: 'Não foi possível salvar essa alteração.',
    readOnly: 'Você pode ler este agente, mas só quem mantém e quem coedita podem alterá-lo.',
    // "convidado" concordaria com o gênero de quem lê, que ninguém aqui conhece; a frase diz o
    // mesmo sem o particípio (D-21).
    readOnlyInvited:
      'Você recebeu um convite para ajudar com este agente. Aceite o convite em Seus agentes para alterá-lo ou conversar com ele.',
    conflictTitle: 'Alguém salvou este agente enquanto você editava',
    conflictBody:
      'Nada do que você fez foi sobrescrito, e nada do que a outra pessoa fez foi perdido. Sua versão continua na tela — copie o que quiser guardar e recarregue para começar da outra versão.',
    conflictReload: 'Recarregar a outra versão',
    conflictKeep: 'Continuar editando a minha'
  },

  owner: {
    label: 'Pertence a',
    move: 'Mover para um grupo…',
    moveAnywhere: 'Mover…',
    moveTitle: (agent) => `Mover ${agent}`,
    moveLead:
      'A pessoa ou o grupo a quem o agente pertence responde por ele. Os agentes de um grupo são mantidos por quem gerencia o grupo.',
    moveRule: 'Você pode mover para um grupo que você gerencia. Um administrador pode mover para qualquer lugar.',
    moveKeeps:
      'Mover nunca muda quem pode usar. Valem os limites de quem passa a ter o agente: se já estiver no máximo de agentes permitido, a mudança é recusada.',
    moveSubmit: 'Mover',
    moving: 'Movendo…',
    cancel: 'Cancelar',
    moveFailed: 'Não foi possível mover.',
    // "gestores" obrigaria a escolher um gênero para pessoas que ninguém aqui conhece; a oração
    // relativa diz o mesmo e serve aos dois, como "Só quem mantém" no ADR-0018.
    managersOf: (group) => `quem gerencia ${group}`,
    noDestination: 'Você não gerencia outro grupo para onde mover. Um administrador pode mover para qualquer lugar.'
  },

  sections: {
    identity: {
      title: 'Identidade',
      lead: 'Como as pessoas reconhecem este agente na galeria e em uma conversa.',
      name: 'Nome',
      namePlaceholder: 'Ajudante de Despesas',
      nameHint: 'Dois agentes compartilhados com as mesmas pessoas não podem ter o mesmo nome.',
      icon: 'Ícone',
      iconPlaceholder: '💼',
      iconHint: 'Um emoji, ou deixe em branco para usar as primeiras letras do nome.',
      accent: 'Cor',
      category: 'Categoria',
      categoryNone: '—',
      tagline: 'Resumo em uma linha',
      taglinePlaceholder: 'Responde dúvidas de despesas com base na política de viagens vigente.',
      taglineHint: 'Aparece no card. Diga o que ele faz por alguém, em uma linha.',
      description: 'Descrição',
      descriptionPlaceholder:
        'Para que serve este agente, para quem ele é, e o que ele não faz. Aparece na página dele, antes de alguém conversar.',
      descriptionHint: 'As pessoas leem isto para decidir se confiam nele. Diga também o que ele não faz.'
    },

    instructions: {
      title: 'Instruções',
      lead: 'Diga ao agente como se comportar, em frases simples, como você orientaria alguém que acabou de chegar.',
      label: 'Instruções',
      placeholder:
        'Você ajuda funcionários com dúvidas sobre despesas.\n\nResponda apenas a partir da política de viagens. Cite a regra que usou. Se alguém perguntar sobre algo que a política não cobre, diga isso e indique financeiro@empresa.com.br.\n\nSeja breve. Duas ou três frases, a menos que peçam detalhes.',
      hint: 'Boas instruções dizem quem ele ajuda, o que fazer quando ele não sabe, e que tom usar.',
      guidance: [
        'Diga para quem ele é e do que essas pessoas costumam precisar.',
        'Diga o que fazer quando a resposta não estiver no conhecimento dele — indicar alguém é uma boa resposta.',
        'Diga que tom ele deve ter: breve, formal, passo a passo.',
        'As regras da plataforma sempre vencem. As instruções não podem fazer ele esconder de onde veio uma resposta.'
      ],
      counted: (fmt, chars) => countOf(fmt, chars, { one: 'caractere', other: 'caracteres' })
    },

    knowledge: {
      title: 'Conhecimento',
      lead: 'As bases de conhecimento de onde ele responde. Anexar uma não compartilha nada: cada pessoa recebe respostas só do que ela pode usar, a menos que quem mantém a base deixe ela responder para todas as pessoas que este agente alcança.',
      loading: 'Procurando conhecimento que você pode anexar…',
      failed: 'Não foi possível carregar o conhecimento que você pode usar. Você ainda pode salvar todo o resto.',
      manage: 'Página de Conhecimento',
      manageHint: 'Crie e mantenha bases de conhecimento na página de Conhecimento.',
      open: (base) => `Abrir ${base} na página de Conhecimento`,
      maintainTitle: 'Você mantém',
      maintainEmpty: 'Você ainda não mantém nenhuma base de conhecimento.',
      useTitle: 'Você pode usar',
      useEmpty: 'Nada mais foi compartilhado com você ainda.',
      emptyHint:
        'Crie uma na página de Conhecimento, ou peça a quem mantém uma base para compartilhar com um grupo do qual você faz parte.',
      otherTitle: 'Anexadas, e que você não pode usar',
      otherLead:
        'Foi anexada antes, ou ela responde por meio deste agente. Você pode remover aqui; não dá para anexar de novo enquanto não for compartilhada com você.',
      unknownBase: 'Uma base de conhecimento que você não pode mais usar',
      ownedBy: (owner) => `Pertence a ${owner}`,
      documents: (fmt, count) => countOf(fmt, count, { one: 'documento', other: 'documentos' }),
      baseSpace: (amount) => `${amount} em uso`,
      underOneMegabyte: 'menos de 1 MB',
      // "Pronta" e não "Pronto": o estado descreve a base de conhecimento, que é feminina.
      state: {
        empty: 'Ainda sem nada',
        reading: 'Lendo arquivos…',
        ready: 'Pronta',
        needs_attention: 'Algo precisa de atenção'
      },
      // O particípio concorda com o número, não só o substantivo: "1 base … anexada" e
      // "3 bases … anexadas". Por isso a frase inteira é escolhida por forma, e não montada
      // em volta de `countOf`.
      attachedCount: (fmt, count) =>
        count === 0
          ? 'Nada anexado — ele vai responder a partir de tudo o que a pessoa já pode usar.'
          : byCount(fmt, count, {
              one: `${fmt.formatCount(count)} base de conhecimento anexada.`,
              other: `${fmt.formatCount(count)} bases de conhecimento anexadas.`
            }),
      checkedAgainst: 'Verificado com base em quem pode usar (etapa 7).',
      unsaved: 'Salve para ver quem recebe respostas dela.',
      coverageLoading: 'Verificando quem recebe respostas destas…',
      coverageFailed: 'Não foi possível verificar se todo mundo recebe respostas destas.',
      coverage: {
        covered: 'Todas as pessoas que ele alcança podem usar esta base.',
        // O aposto nomeia o PAPEL, que é masculino para qualquer pessoa que o tenha — é a
        // mesma forma que `knowledge` usa ao dizer quem compartilhou (D-21).
        lent: (by, byAdministrator = false) =>
          by === null
            ? 'Ele responde a partir desta base para todas as pessoas que alcança — quem mantém a base permitiu.'
            : byAdministrator
              ? `Ele responde a partir desta base para todas as pessoas que alcança — permitido por ${by}, no papel de administrador.`
              : `Ele responde a partir desta base para todas as pessoas que alcança — permitido por ${by}.`,
        missingEveryone: (fmt, count) =>
          byCount(fmt, count, {
            one: `Nem todas as pessoas que entram podem usar esta base: ${people(fmt, count)} vai receber respostas sem ela.`,
            other: `Nem todas as pessoas que entram podem usar esta base: ${people(fmt, count)} vão receber respostas sem ela.`
          }),
        missingGroups: (groups) => `Quem está em ${groups} não pode usar esta base, então recebe respostas sem ela.`,
        missingPeople: (fmt, count) =>
          byCount(fmt, count, {
            one: `${people(fmt, count)} que ele alcança não pode usar esta base, então recebe respostas sem ela.`,
            other: `${people(fmt, count)} que ele alcança não podem usar esta base, então recebem respostas sem ela.`
          })
      }
    },

    capabilities: {
      title: 'O que ele pode fazer',
      lead: 'Além de responder, isto é tudo o que o agente pode acionar. Ele sempre pergunta antes de agir.',
      empty: 'Nada foi disponibilizado ainda para quem cria agentes.',
      emptyHint: 'Um administrador publica estes itens, com uma explicação do que cada um faz.',
      loading: 'Carregando o que este agente poderia fazer…',
      failed: 'Não foi possível carregar essa lista. Você ainda pode salvar todo o resto.',
      reads: 'Lê informações',
      acts: 'Pode executar ações — sempre pergunta antes',
      otherSystem: 'Na plataforma',
      selectedCount: (fmt, count) =>
        count === 0
          ? 'Nada selecionado — ele vai apenas responder, e nada mais.'
          : byCount(fmt, count, {
              one: `${fmt.formatCount(count)} item selecionado.`,
              other: `${fmt.formatCount(count)} itens selecionados.`
            })
    },

    behavior: {
      title: 'Comportamento',
      lead: 'Duas escolhas que mudam o que as pessoas recebem de volta.',
      groundedLabel: 'Responder só a partir do conhecimento dele',
      groundedOn:
        'Ele vai dizer que não sabe em vez de chutar quando a resposta não estiver nos documentos que você anexou.',
      groundedOff:
        'Ele responde primeiro a partir dos documentos, e pode acrescentar o que sabe de modo geral — dizendo o que é o quê.',
      groundedWarning:
        'Anexe pelo menos uma base de conhecimento antes de publicar, ou este agente não vai ter de onde responder.',
      memoryLabel: 'Lembra preferências',
      memoryOn: 'As pessoas podem dizer como preferem que as coisas sejam feitas aqui, e ele lembra da próxima vez.',
      memoryOff:
        'Ele começa do zero a cada conversa, fora as preferências que cada pessoa salvou para si em toda a plataforma.'
    },

    starters: {
      title: 'Perguntas iniciais',
      lead: 'Até quatro perguntas de exemplo. Tocar em uma preenche a caixa de mensagem — nunca envia sozinha.',
      placeholder: 'O que posso lançar como despesa em um jantar com cliente?',
      add: 'Adicionar uma pergunta',
      remove: (index) => `Remover pergunta ${index + 1}`,
      full: 'Quatro é o máximo.'
    },

    audience: {
      title: 'Quem pode usar',
      lead: 'Nada muda até você publicar. Os grupos que você gerencia entram no ar nesse momento; outros grupos, e todas as pessoas, aguardam a revisão de um administrador.',
      leadAdmin:
        'Nada muda até você publicar. Você tem o papel de administrador, então o que escolher entra no ar nesse momento.',
      locked: 'Só quem mantém pode escolher quem pode usar.',
      gap: (base, whom) => `${base} não alcança ${whom}`,
      gapEveryone: (fmt, count) =>
        byCount(fmt, count, {
          one: `${people(fmt, count)} que entra`,
          other: `${people(fmt, count)} que entram`
        }),
      gapPeople: (fmt, count) => `${people(fmt, count)} que ele alcança`,
      seeKnowledge: 'ver Conhecimento'
    }
  },

  copyPolicy: {
    title: 'Cópias',
    lead: 'Se outras pessoas podem criar o próprio agente a partir deste.',
    auto: 'Acompanhar quem pode usar',
    autoHint: 'Permitido quando todas as pessoas podem usar, desligado quando é compartilhado com grupos específicos.',
    allowed: 'Permitido',
    allowedHint: 'Quem pode usar também pode fazer a própria cópia para alterar.',
    blocked: 'Não permitido',
    blockedHint: 'Ninguém pode copiar.'
  },

  lint: {
    title: 'Vale corrigir antes de publicar',
    blockingTitle: 'Corrija isto antes de publicar',
    blocking: 'Impede publicar',
    suggestion: 'Sugestão',
    field: (field) => `em ${field}`,
    heading: {
      instructions_empty: 'As instruções estão vazias',
      instructions_short: 'As instruções estão muito curtas',
      contradicts_platform: 'Isto pede algo que a plataforma não vai fazer',
      impersonation: 'Isto soa como se ele falasse por um grupo ou uma área que não representa',
      trust_mark: 'Isto alega um status que só um administrador pode conceder',
      reserved_name: 'Esse nome é reservado',
      legacy_setting_removed: 'Algumas configurações agora são gerenciadas para você'
    }
  },

  health: {
    title: 'Precisa da sua atenção',
    ok: 'Está tudo funcionando',
    okBody: 'O conhecimento dele está acessível e tudo o que ele pode fazer está disponível.',
    // `when` já vem de `Intl.RelativeTimeFormat`, que em português carrega a própria preposição
    // ("há 5 min"). Acrescentar "em" aqui renderizaria "verificado em há 5 min".
    checkedAt: (when) => `verificado ${when}`,
    heading: {
      knowledge_missing: 'Falta uma base de conhecimento',
      capability_disabled: 'Algo que ele pode fazer foi desligado',
      capability_quarantined: 'Algo que ele pode fazer está pausado',
      layer_dropped: 'Parte da configuração dele não pôde ser aplicada',
      coverage_gap: 'Parte das pessoas que ele alcança fica sem o conhecimento dele',
      owner_deactivated: 'Este agente precisa de quem responda por ele'
    },
    remedy: {
      knowledge_missing: 'Remova abaixo, ou peça a quem mantém para restaurar. As respostas continuam enquanto isso.',
      capability_disabled:
        'Não há nada que você possa mudar aqui — um administrador desligou para todo mundo. Remova se não for mais necessário.',
      capability_quarantined:
        'Um administrador pausou enquanto verifica uma mudança. Volta sozinho, ou você pode remover.',
      layer_dropped: 'Salve o agente de novo. Se voltar a acontecer, um administrador precisa olhar.',
      coverage_gap:
        'Algumas pessoas que ele alcança não podem usar uma das bases de conhecimento, então recebem respostas sem ela. Conhecimento, abaixo, diz quem — e deixa você pedir a quem mantém a base.',
      owner_deactivated:
        'Ninguém mantém este agente agora. Um administrador pode movê-lo para uma pessoa ou um grupo. Ele continua respondendo enquanto isso.'
    }
  },

  preview: {
    title: 'Prévia',
    caption: 'Prévia — as ações são simuladas',
    captionHint: 'Isto executa a versão que está na tela, com as mesmas regras do real. Nada do que ele faz sai daqui.',
    start: 'Iniciar uma prévia',
    starting: 'Abrindo uma prévia…',
    startFailed: 'Não foi possível iniciar a prévia.',
    retry: 'Tentar novamente',
    saveFirst: 'Salve suas alterações para testá-las aqui.',
    placeholder: 'Pergunte alguma coisa…',
    send: 'Enviar',
    stop: 'Parar',
    thinking: 'pensando…',
    empty: 'Faça uma pergunta como outra pessoa faria.',
    simulated: 'Simulado — aprovar aqui não muda nada fora da prévia.',
    // "execução" é feminina, então o particípio e o demonstrativo concordam com ela.
    failedRun: 'Essa execução da prévia não terminou.',
    quota: 'Você usou todas as prévias de hoje. Elas são renovadas amanhã.'
  },

  versions: {
    title: 'Histórico',
    lead: 'Todo salvamento fica guardado. Você pode voltar para qualquer um deles.',
    open: 'Histórico',
    close: 'Ocultar histórico',
    loading: 'Carregando histórico…',
    empty: 'Nada salvo ainda.',
    noNote: 'sem nota',
    restore: 'Voltar para esta',
    restoring: 'Restaurando…',
    restoredTitle: 'Restaurado',
    restoredBody: (version) => `Você está editando o agente como ele estava no salvamento ${version}.`,
    droppedTitle: 'Parte não pôde voltar',
    droppedBody: 'Estes itens não estão mais disponíveis para você, então ficaram de fora:',
    lintTitle: 'Esta versão mais antiga precisa de uma olhada',
    failed: 'Não foi possível restaurar essa versão.'
  },

  editors: {
    title: 'Coeditores',
    leadOwner:
      'Pessoas que podem alterar este agente com você. Só você pode publicar, escolher quem pode usar ou mover.',
    // "você incluído" concordaria com o gênero de quem lê; "e você é uma dessas pessoas" diz o
    // mesmo e serve aos dois.
    leadManager: (group) =>
      `Pessoas que podem alterar este agente junto com quem gerencia ${group}. Só quem gerencia o grupo — e você é uma dessas pessoas — pode publicar, escolher quem pode usar ou mover.`,
    leadOther: (owner) =>
      `Pessoas que podem alterar este agente. Só ${owner} pode publicar, escolher quem pode usar ou mover.`,
    leadOtherGroup: (group) =>
      `Pessoas que podem alterar este agente. Só quem gerencia ${group} pode publicar, escolher quem pode usar ou mover.`,
    leadAdmin: (owner) => `Pessoas que podem alterar este agente. ${owner} responde por ele.`,
    invite: 'Convidar',
    inviting: 'Convidando…',
    emailLabel: 'E-mail de trabalho da pessoa',
    emailPlaceholder: 'colega@empresa.com.br',
    invited: 'Convite enviado — aguardando aceite',
    accepted: 'Edita com você',
    remove: (name) => `Remover ${name}`,
    empty: 'Mais ninguém por enquanto.',
    failed: 'Não foi possível enviar esse convite.',
    // "Responsável" serve aos dois gêneros, ao contrário de "dono/dona", e é a palavra que a
    // própria frase "Pertence a" já implica.
    owner: 'Responsável'
  },

  invites: {
    title: 'Convites',
    lead: 'Pessoas que pediram sua ajuda com o agente delas. Você pode olhar primeiro; altera e conversa com ele depois de aceitar.',
    fromPerson: (owner) => `${owner} convidou você`,
    fromGroup: (group) => `Pertence a ${group} — quem gerencia o grupo convidou você`,
    accept: 'Aceitar',
    decline: 'Recusar',
    look: 'Olhar primeiro',
    pending: 'Processando…',
    failed: 'Não foi possível responder. Tente de novo.'
  },

  mine: {
    title: 'Seus agentes',
    lead: 'Tudo o que pertence a você, o que você mantém para um grupo, e o que você ajuda a editar.',
    create: 'Novo agente',
    empty: 'Você ainda não criou um agente.',
    emptyBody: 'Um agente é um nome, algumas frases de instrução, e os documentos de onde ele deve responder.',
    loading: 'Carregando seus agentes…',
    failed: 'Não foi possível carregar seus agentes.',
    open: 'Abrir',
    coEditing: 'Você coedita',
    attention: 'Precisa de atenção',
    waitingForReview: 'Aguardando revisão',
    audienceEveryone: 'todas as pessoas que entram',
    audienceGroups: (fmt, groups) => fmt.formatList(groups) || 'ninguém ainda',
    updated: (when) => `atualizado ${when}`
  },

  accents: {
    slate: 'Ardósia',
    teal: 'Turquesa',
    plum: 'Ameixa',
    moss: 'Musgo',
    navy: 'Marinho',
    clay: 'Argila'
  },

  insights: {
    open: 'Desempenho',
    close: 'Ocultar desempenho',
    title: 'Como este agente está indo',
    lead: 'Contagens, taxas e o que as pessoas escolheram enviar para você. Você nunca vê a conversa de ninguém.',
    loading: 'Carregando…',
    unavailable: 'Ainda não há desempenho para mostrar',
    unavailableBody: 'Nada foi registrado para este agente, ou esta instalação não habilitou a visão de quem mantém.',

    conversations: 'Conversas',
    people: 'Pessoas que usaram',
    helpful: 'Útil',
    unhelpful: 'Não útil',
    helpfulShare: (share) => `${share} das avaliações foram úteis`,
    noRatings: 'Ninguém avaliou uma resposta ainda.',

    zeroHitTitle: 'Perguntas sem resultado',
    zeroHitHint: 'Ele buscou no que pode ler e não achou nada. Em geral, é um documento que falta.',
    abstentionTitle: 'Perguntas sem resposta',
    abstentionHint: 'Ele disse que não sabia em vez de chutar. Parte disto é o agente funcionando certo.',
    notMeasurable: 'Uso insuficiente para dizer',

    versionsTitle: 'Avaliações por versão',
    versionsHint: 'Compare a versão que você acabou de compartilhar com a anterior.',
    versionLabel: (version) => `Versão ${version}`,
    versionUnknown: 'Antes de ser compartilhado',
    // A assinatura não recebe idioma, e "1 úteis" estaria errado — então a linha vira rótulo e
    // valor, que não concorda com nada e ainda cabe na coluna estreita em que ela vive.
    ratingsLine: (helpful, unhelpful) => `útil: ${helpful} · não útil: ${unhelpful}`,
    versionsEmpty: 'Nenhuma avaliação ainda.',

    notesTitle: 'O que as pessoas escreveram',
    notesHint: 'Sem nomes, e nunca antes de um dia depois de escritos.',
    notesEmpty: 'Nenhuma nota ainda.',

    questionsTitle: 'Perguntas enviadas para você',
    questionsHint: 'Cada uma é uma pergunta que alguém escolheu compartilhar porque a resposta não deu conta.',
    questionsEmpty: 'Ninguém enviou uma pergunta para você ainda.',
    questionOpen: 'Aguardando você',
    questionResolved: 'Resolvida',

    healthTitle: 'O que precisou de atenção',
    healthEmpty: 'Nada deu errado com este agente.',

    knowledgeTitle: 'Qual material seu é usado',
    knowledgeHint:
      'Anexar não é usar. Uma base de conhecimento pode estar anexada, compartilhada e cheia, e mesmo assim nunca ser a origem de uma resposta.',
    knowledgeEmpty: 'Nada está anexado a este agente, então as respostas dele vêm do modelo, não dos seus documentos.',
    knowledgeSearches: 'Buscas feitas',
    knowledgeFoundNothing: 'Buscas sem resultado',
    knowledgeGrounded: 'Respostas com seus documentos',
    knowledgeGroundedHint: (fmt, grounded, answers) =>
      byCount(fmt, answers, {
        one: `${grounded} de ${answers} resposta`,
        other: `${grounded} de ${answers} respostas`
      }),
    knowledgeWindow: (fmt, days) =>
      byCount(fmt, days, { one: `No último ${days} dia.`, other: `Nos últimos ${days} dias.` }),
    baseHolding: (fmt, documents, space) =>
      `${byCount(fmt, documents, { one: `${documents} documento`, other: `${documents} documentos` })} · ${space}`,
    baseUsed: (fmt, answers) =>
      byCount(fmt, answers, { one: `respondeu ${answers} pergunta`, other: `respondeu ${answers} perguntas` }),
    // "usada" e não "usado": o estado descreve a base de conhecimento.
    baseUnused: 'Ainda não usada',
    baseUnusedHint:
      'Vale olhar antes de excluir — ela pode ter a resposta de uma pergunta que ninguém fez neste mês, ou você pode tê-la adicionado agora há pouco.',
    baseLastUsed: (when) => `última vez ${when}`,
    baseOwnedBy: (owner) => `pertence a ${owner}`
  }
}
